import Icon from '@ant-design/icons';
import { Modal, message } from 'antd';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import DoneIcon from '@/components/Icon/svgs/check_circle.svg';
import RightSvg from '@/components/Icon/svgs/right.svg';
import UndoneIcon from '@/components/Icon/svgs/todo_undone.svg';
import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import DotsLoading from '@/components/dots-loading';
import { useModel } from '@/hooks/useModel';
import { useTranslation } from '@/i18n/client';
import type { IGroupUserInfo, IParticipantUserInfo } from '@/service/friend';
import { getCircleMemberList, getSelector } from '@/service/friend';

import styles from './index.less';

// 阻止事件冒泡到父组件
const stopPropagationModalRender = (node: ReactNode) => (
  <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
    {node}
  </div>
);

interface IProps {
  className?: string;
  // Modal 控制
  open: boolean;
  setOpen: (open: boolean) => void;
  // 选择相关
  selectedIds?: string[];
  handleSelect: (member: IParticipantUserInfo) => void;
  onCreate?: () => void;
  createLoading?: boolean;
  // getSelector API 的 参数
  participantType?: 'friend' | 'circle_friend';
  checkTypes?: string[];
  circleRoute?: string;
  // 自定义标题
  title?: string;
  // 自定义确认按钮文案
  confirmText?: string;
  // 自定义加载中文案
  loadingText?: string;
  // Modal zIndex
  zIndex?: number;
  // Modal 宽度
  width?: number | string;
  // 是否是转让模式（使用 getCircleMemberList 接口）
  isTransferMode?: boolean;
}

const PAGE_SIZE = 20;

const GroupMemberSelect = (props: IProps) => {
  const {
    className,
    open,
    setOpen,
    handleSelect,
    selectedIds = [],
    onCreate,
    createLoading,
    participantType = 'friend',
    checkTypes,
    circleRoute,
    title,
    confirmText,
    loadingText,
    zIndex = 1100,
    width = 1000,
    isTransferMode = false
  } = props;

  const { t } = useTranslation();

  const paginationRef = useRef({ pageNo: 1, pageSize: PAGE_SIZE });
  const [memberList, setMemberList] = useState<IParticipantUserInfo[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [noMore, setNoMore] = useState<boolean>(false);

  const { userProfile } = useModel('auth-model', (model) => ({ userProfile: model.userProfile }));

  // 关闭弹窗
  const handleClose = () => {
    setOpen(false);
  };

  // 转让模式：使用 getCircleMemberList 接口
  const handleGetTransferMemberList = () => {
    if (!circleRoute) return;

    setLoading(true);
    setNoMore(true); // 转让模式不需要分页

    getCircleMemberList({
      route: circleRoute,
      adminOnly: false
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        const allMembers = res.data.data.list;

        // 过滤掉需要排除的用户（通常是群主自己）
        const filteredMembers = allMembers.filter(
          (m: IGroupUserInfo) => m.id !== userProfile?.userId
        );

        // 转换为 IParticipantUserInfo 格式
        const convertedMembers: IParticipantUserInfo[] = filteredMembers.map(
          (m: IGroupUserInfo) => ({
            id: m.id,
            username: m.username,
            avatar: m.avatar,
            route: m.route,
            available: true,
            nickname: m.nickname
          })
        );

        setMemberList(convertedMembers);
      })
      .catch((e) => {
        message.error(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleGetMemberList = (isRefresh: boolean = false) => {
    // 如果是转让模式，使用不同的接口
    if (isTransferMode) {
      handleGetTransferMemberList();

      return;
    }

    if (isRefresh) {
      paginationRef.current.pageNo = 1;
      setNoMore(false);
    }

    const { pageNo, pageSize } = paginationRef.current;
    const isFirstPage = pageNo === 1;

    isFirstPage ? setLoading(true) : setLoadingMore(true);

    // 构建请求参数
    const params =
      participantType === 'friend'
        ? {
            participantType: 'friend' as const,
            checkTypes: checkTypes?.length ? checkTypes : undefined,
            circleRoute,
            pageNo,
            pageSize
          }
        : {
            participantType: participantType as 'circle_friend',
            circleRoute: circleRoute!,
            checkTypes: checkTypes?.length ? checkTypes : undefined,
            pageNo,
            pageSize
          };

    getSelector(params)
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        const responseData = (res.data.data as { list: IParticipantUserInfo[]; total: number })
          .list;

        paginationRef.current.pageNo = pageNo + 1;

        if (isFirstPage) {
          setMemberList(responseData || []);
        } else {
          setMemberList((prev) => [...(prev || []), ...(responseData || [])]);
        }

        // 判断是否有更多数据：如果返回的数据少于 pageSize，说明没有更多了
        if (responseData.length < pageSize) {
          setNoMore(true);
        }
      })
      .catch((e) => {
        message.error(e.message);
      })
      .finally(() => {
        isFirstPage ? setLoading(false) : setLoadingMore(false);
      });
  };

  // 使用 JSON.stringify 避免数组引用变化导致重新获取
  const checkTypesKey = checkTypes ? JSON.stringify(checkTypes) : '';

  // 每次打开弹窗时重新获取数据
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) {
      handleGetMemberList(true);
    }
  }, [open, participantType, checkTypesKey, circleRoute, isTransferMode]);

  const isSelected = (id: string) => selectedIds.includes(id);

  // 判断是否禁用（available 为 false 时禁用）
  const isDisabled = (item: IParticipantUserInfo) => item.available === false;

  // 判断是否显示选中状态（已选中或禁用状态都显示选中）
  const isChecked = (item: IParticipantUserInfo) => isSelected(item.id) || isDisabled(item);

  return (
    <Modal
      centered
      className={styles.selectModal}
      closable={false}
      destroyOnClose
      footer={null}
      maskClosable
      modalRender={stopPropagationModalRender}
      onCancel={handleClose}
      open={open}
      width={width}
      zIndex={zIndex}
    >
      <div className={classNames(styles.container, className)}>
        <div className={styles.headerContainer}>
          <div className={styles.backBtn} onClick={handleClose}>
            <Icon className={styles.backIcon} component={RightSvg} />
          </div>
          <div className={styles.title}>{title || t('friend.newGroup')}</div>
          <div className={styles.placeholder} />
        </div>

        <div className={styles.contentContainer}>
          <div className={styles.memberContainer}>
            <div className={styles.memberTitle}>{t('friend.recentChats')}</div>
            {loading || memberList === undefined ? (
              <LottieJumpLoading className="!w-16 !h-16 mt-20" />
            ) : memberList.length > 0 ? (
              memberList.map((item) => (
                <div
                  key={item.id}
                  className={classNames(styles.memberItem, {
                    [styles.active]: isChecked(item),
                    [styles.disabled]: isDisabled(item)
                  })}
                  onClick={() => {
                    // 禁用状态不可点击
                    if (isDisabled(item)) return;

                    handleSelect(item);
                  }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img alt={item.username} className={styles.avatar} src={item.avatar} />
                  <div className="flex-1 w-0 flex flex-col">
                    <div className="text-sm leading-6 font-medium text-[#231309]">
                      {item.nickname || item.username}
                    </div>
                  </div>
                  <div className={styles.iconWrapper}>
                    {isChecked(item) ? (
                      <Icon className={styles.icon} component={DoneIcon} />
                    ) : (
                      <Icon
                        className={classNames(styles.icon, styles.undone)}
                        component={UndoneIcon}
                      />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-sm leading-6 text-[#756C5D] mt-10">
                {t('common.noResult')}
              </div>
            )}

            {!noMore &&
              !loading &&
              (loadingMore ? (
                <DotsLoading className="mt-[4px]" />
              ) : (
                <div
                  className="text-center text-sm underline leading-6 text-[#756C5D] cursor-pointer"
                  onClick={() => {
                    handleGetMemberList();
                  }}
                >
                  {t('wiki.detailSubpage.showMore')}
                </div>
              ))}
          </div>
        </div>

        {/* 确认按钮 */}
        <div
          className={classNames(styles.createBtn, {
            [styles.disabled]: createLoading || selectedIds.length === 0
          })}
          onClick={() => {
            if (createLoading || selectedIds.length === 0) return;

            onCreate?.();
          }}
        >
          {createLoading ? loadingText || t('error.loading') : confirmText}
        </div>
      </div>
    </Modal>
  );
};

export default GroupMemberSelect;
